import { InfoOutlined } from '@mui/icons-material';
import {
    type AlertProps,
    Button,
    CircularProgress,
    Link,
    Alert as MUIAlert,
    Stack,
    type SxProps,
    type Theme,
} from '@mui/material';

// Define custom severity types
type CustomSeverity = 'surface' | 'surfaceVariant' | 'blue' | 'danger';

// Standardized action buttons. Two supported shapes:
//  - two-button CTA: a `secondary` (outlined) + a `primary` (contained), or a single `primary`
//  - link: a single low-emphasis `link` (underlined text button)
type AlertActionKind = 'primary' | 'secondary' | 'link';

export interface AlertAction {
    label: React.ReactNode;
    onClick: () => void;
    kind: AlertActionKind;
    disabled?: boolean;
    loading?: boolean;
    key?: string;
}

interface CustomAlertProps extends AlertProps {
    severity: CustomSeverity;
    actions?: AlertAction[];
    children?: React.ReactNode;
}

const actionVariant: Record<AlertActionKind, 'contained' | 'outlined' | 'text'> = {
    primary: 'contained',
    secondary: 'contained',
    link: 'text',
};

const actionSx: Record<AlertActionKind, SxProps> = {
    primary: {},
    secondary: { color: (t) => t.palette.secondary.main, background: 'white !important' },
    link: {
        textDecoration: 'underline',
        cursor: 'pointer',
        '&[disabled]': { cursor: 'inherit', color: (t) => t.palette.primary.mui.inactive },
    },
};

// Define colors for the new severities
const severityStyles: Record<
    CustomSeverity,
    { color: ((t: Theme) => string) | string; backgroundColor: ((t: Theme) => string) | string }
> = {
    surface: { color: (t: Theme) => t.palette.primary.main, backgroundColor: '#FFF' },
    surfaceVariant: { color: (t: Theme) => t.palette.primary.main, backgroundColor: '#F5F5F5' },
    blue: { color: (t: Theme) => t.palette.primary.onSurface, backgroundColor: '#E7F8FF' },
    danger: { color: '#974401', backgroundColor: '#FFE4CD' },
};

const Alert = ({ severity, sx, actions, children, ...props }: CustomAlertProps) => {
    const { color, backgroundColor } = severityStyles[severity || 'surface'];

    return (
        <MUIAlert
            {...props}
            iconMapping={{
                success: <InfoOutlined sx={{ fontSize: 22 }} />, // Because the custom severities doesn't overlap with the MUI ones, in the backgroud all Alerts are using 'success'
            }}
            sx={{
                color,
                backgroundColor,
                alignItems: 'center',
                fontSize: (theme) => theme.typography.body2.fontSize,
                '.MuiAlert-icon': { color, alignSelf: 'start' },
                '.MuiAlert-message': {
                    py: 0,
                    width: '100%',
                    display: 'flex',
                },
                borderRadius: 0,
                py: 1,
                ...sx, // Allow additional styles from props
            }}
        >
            <Stack
                direction='row'
                width='100%'
                alignItems='center'
                flexWrap='wrap'
                justifyContent='space-between'
                useFlexGap
                spacing={1}
            >
                {children}
                {actions && actions.length > 0 && (
                    <Stack direction='row' spacing={1} alignItems='center'>
                        {actions.map(({ label, onClick, kind, disabled, loading, key }, index) =>
                            kind === 'link' ? (
                                <Link
                                    key={key ?? index}
                                    onClick={disabled || loading ? undefined : onClick}
                                    disabled={disabled || loading}
                                    sx={actionSx[kind]}
                                >
                                    {label}
                                </Link>
                            ) : (
                                <Button
                                    key={key ?? index}
                                    variant={actionVariant[kind]}
                                    color='secondary'
                                    size='small'
                                    onClick={onClick}
                                    disabled={disabled || loading}
                                    startIcon={loading ? <CircularProgress size={16} color='inherit' /> : undefined}
                                    sx={actionSx[kind]}
                                >
                                    {label}
                                </Button>
                            )
                        )}
                    </Stack>
                )}
            </Stack>
        </MUIAlert>
    );
};

export default Alert;
